import type { ArtistId } from 'types/artist'
import type { ClientLibraryArtist, ClientLibraryId } from 'types/client-library'
import type { Thunk } from 'types/redux'
import type { TrackId } from 'types/track'
import { mapResponseToReducerObjects } from '@disco/state-management/actions/PlaylistTrackActions'
import api from '@disco/api/server'
import {
  CLIENT_LIBRARY_ARTIST_LOAD,
  CLIENT_LIBRARY_ARTIST_LOAD_FAIL,
  CLIENT_LIBRARY_ARTIST_LOAD_SUCCESS,
  CLIENT_LIBRARY_ARTIST_TRACKS_LOAD,
  CLIENT_LIBRARY_ARTIST_TRACKS_LOAD_FAIL,
  CLIENT_LIBRARY_ARTIST_TRACKS_LOAD_SUCCESS,
  CLIENT_LIBRARY_ARTIST_TRACKS_UPDATE,
  CLIENT_LIBRARY_ARTIST_TRACKS_UPDATE_FAIL,
  CLIENT_LIBRARY_ARTIST_TRACKS_UPDATE_SUCCESS,
  CLIENT_LIBRARY_ARTIST_UPDATE,
  CLIENT_LIBRARY_ARTIST_UPDATE_FAIL,
  CLIENT_LIBRARY_ARTIST_UPDATE_SUCCESS,
  CLIENT_LIBRARY_ARTISTS_LOAD,
  CLIENT_LIBRARY_ARTISTS_LOAD_FAIL,
  CLIENT_LIBRARY_ARTISTS_LOAD_NEXT,
  CLIENT_LIBRARY_ARTISTS_LOAD_NEXT_FAIL,
  CLIENT_LIBRARY_ARTISTS_LOAD_NEXT_SUCCESS,
  CLIENT_LIBRARY_ARTISTS_LOAD_SUCCESS,
  CLIENT_LIBRARY_FEATURED_ARTISTS_SET,
  CLIENT_LIBRARY_FEATURED_ARTISTS_SET_FAIL,
  CLIENT_LIBRARY_FEATURED_ARTISTS_SET_SUCCESS,
} from '@disco/constants/ActionTypes'
import { omit } from '@disco/utils/array'
import { pushUINotification } from '@disco/utils/notifications'

const explainError = (response, state) => {
  const error = { ...response }

  // TODO: Remove when API response will be fixed
  if (error['related_artist_ids']) {
    error['related_artist_ids'] = error['related_artist_ids'].map(message => {
      const artistId = message.match(/\d+/)
      const artist = state.artists.items[artistId]
      return `${artist ? artist.name : 'Artist'} cannot be set as related`
    })
  }

  return Object.values(error).join(' ')
}

/**
 * Loads artists for a client library with optional search and featured filtering.
 * @param libraryId - ID of the client library to load artists for
 * @param query - Search query to filter artists
 * @param isFeatured - Optional flag to filter only featured artists
 * @returns Thunk that loads artists and handles success/error states
 */
export function clientLibraryArtistsLoad(
  libraryId: ClientLibraryId,
  query: string,
  isFeatured?: boolean,
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_ARTISTS_LOAD,
      libraryId,
    })

    try {
      const response = await api.getClientLibraryArtists(
        libraryId,
        query,
        isFeatured,
      )
      dispatch({
        type: CLIENT_LIBRARY_ARTISTS_LOAD_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_ARTISTS_LOAD_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Loads the next page of artists for a client library using pagination.
 * @param libraryId - ID of the client library to load next artists for
 * @param url - Pagination URL for the next page of artists
 * @returns Thunk that loads next page of artists
 */
export function clientLibraryArtistsLoadNext(
  libraryId: ClientLibraryId,
  url: string,
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_ARTISTS_LOAD_NEXT,
      libraryId,
    })

    try {
      const response = await api.getUrl(
        url,
        null,
        'api.getClientLibraryArtistsLoadNext',
      )
      dispatch({
        type: CLIENT_LIBRARY_ARTISTS_LOAD_NEXT_SUCCESS,
        libraryId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_ARTISTS_LOAD_NEXT_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Loads detailed information for a specific artist in a client library.
 * @param libraryId - ID of the client library containing the artist
 * @param artistId - ID of the artist to load
 * @returns Thunk that loads artist details and handles success/error states
 */
export function clientLibraryArtistLoad(
  libraryId: ClientLibraryId,
  artistId: ArtistId,
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_ARTIST_LOAD,
      libraryId,
      artistId,
    })

    try {
      const response = await api.getClientLibraryArtist(libraryId, artistId)
      dispatch({
        type: CLIENT_LIBRARY_ARTIST_LOAD_SUCCESS,
        libraryId,
        artistId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_ARTIST_LOAD_FAIL,
        libraryId,
        artistId,
        error,
      })
    }
  }
}
/**
 * Updates artist information in a client library.
 * @param libraryId - ID of the client library containing the artist
 * @param artistId - ID of the artist to update
 * @param data - Partial artist data to update
 * @returns Thunk that updates artist and shows success/error notifications
 */
export function clientLibraryArtistUpdate(
  libraryId: ClientLibraryId,
  artistId: ArtistId,
  data: Partial<ClientLibraryArtist>,
): Thunk {
  return async (dispatch, getState) => {
    const artist = getState().artists.items[artistId]
    dispatch({
      type: CLIENT_LIBRARY_ARTIST_UPDATE,
      libraryId,
      artistId,
      data,
    })

    try {
      await api.updateClientLibraryArtist(libraryId, artistId, data)
      dispatch({
        type: CLIENT_LIBRARY_ARTIST_UPDATE_SUCCESS,
        libraryId,
        artistId,
        data,
      })
      pushUINotification({
        message: 'Artist updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_ARTIST_UPDATE_FAIL,
        libraryId,
        artistId,
        artist,
      })
      pushUINotification({
        message: explainError(error.body, getState()),
        level: 'error',
      })
    }
  }
}
/**
 * Sets the complete list of featured artists for a client library.
 * @param libraryId - ID of the client library to set featured artists for
 * @param artistIds - Array of artist IDs to set as featured artists
 * @returns Thunk that sets featured artists and shows success notification
 */
export function clientLibraryFeaturedArtistsSet(
  libraryId: ClientLibraryId,
  artistIds: ArtistId[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_FEATURED_ARTISTS_SET,
      libraryId,
      artistIds,
    })

    try {
      await api.setClientLibraryFeaturedArtists(libraryId, artistIds)
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_ARTISTS_SET_SUCCESS,
        libraryId,
      })
      pushUINotification({
        message: 'Artists updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_FEATURED_ARTISTS_SET_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Loads tracks for a specific artist in a client library with optional pagination.
 * @param libraryId - ID of the client library containing the artist
 * @param artistId - ID of the artist to load tracks for
 * @param next - Optional pagination URL for next page of tracks
 * @returns Thunk that loads artist tracks and handles success/error states
 */
export function clientLibraryArtistTracksLoad(
  libraryId: ClientLibraryId,
  artistId: ArtistId,
  next?: string,
): Thunk {
  return async (dispatch, getState) => {
    dispatch({
      type: CLIENT_LIBRARY_ARTIST_TRACKS_LOAD,
      libraryId,
      artistId,
      next,
    })

    try {
      let response

      if (next) {
        response = await api.getUrl(
          next,
          null,
          'api.getClientLibraryArtistTracks',
        )
      } else {
        response = await api.getClientLibraryArtistTracks(libraryId, artistId)
      }

      mapResponseToReducerObjects(dispatch, getState, {
        ...response,
        results: response.results.map(item =>
          omit(item, ['is_featured', 'order']),
        ),
      })
      dispatch({
        type: CLIENT_LIBRARY_ARTIST_TRACKS_LOAD_SUCCESS,
        libraryId,
        artistId,
        response,
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_ARTIST_TRACKS_LOAD_FAIL,
        libraryId,
        error,
      })
    }
  }
}
/**
 * Updates the track list for a specific artist in a client library.
 * @param libraryId - ID of the client library containing the artist
 * @param artistId - ID of the artist to update tracks for
 * @param trackIds - Array of track IDs to set for the artist
 * @returns Thunk that updates artist tracks and shows success notification
 */
export function clientLibraryArtistTracksUpdate(
  libraryId: ClientLibraryId,
  artistId: ArtistId,
  trackIds: TrackId[],
): Thunk {
  return async dispatch => {
    dispatch({
      type: CLIENT_LIBRARY_ARTIST_TRACKS_UPDATE,
      libraryId,
      artistId,
      trackIds,
    })

    try {
      await api.updateClientLibraryArtistTracks(libraryId, artistId, trackIds)
      dispatch({
        type: CLIENT_LIBRARY_ARTIST_TRACKS_UPDATE_SUCCESS,
        libraryId,
        artistId,
        trackIds,
      })
      pushUINotification({
        message: 'Artist updated!',
        level: 'success',
      })
    } catch (error) {
      dispatch({
        type: CLIENT_LIBRARY_ARTIST_TRACKS_UPDATE_FAIL,
        libraryId,
        error,
      })
    }
  }
}
